import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBuilding } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import BudgetEditor from './components/BudgetEditor';

const BudgetEdit = () => {
  const { id, budgetId } = useParams();
  const [searchParams] = useSearchParams();
  const slotId = budgetId ? null : searchParams.get('slot');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['administrateur_general', 'administrateur_general_superieur'].includes(user?.role);

  const [budget, setBudget] = useState(null);
  const [slot, setSlot] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const isNew = !budgetId;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        // Load project
        const pRes = await api.get(`/projects/${id}`);
        setProject(pRes.data.data);

        // Load slots to find the right one
        const sRes = await api.get(`/projects/${id}/slots`);
        const data = sRes.data.data || {};
        const allSlots = [
          ...(data.waves || []).flatMap(w => (w.survey_slots || []).map(s => ({ ...s, waveName: w.name }))),
          ...(data.standalone_slots || []).map(s => ({ ...s, waveName: null })),
        ];

        if (budgetId) {
          // Edit mode: load existing budget
          const bRes = await api.get(`/projects/${id}/budgets/${budgetId}`);
          setBudget(bRes.data.data);
          const matchSlot = allSlots.find(s => s.id === bRes.data.data.survey_slot_id);
          setSlot(matchSlot || null);
        } else if (slotId) {
          // Create mode: find the slot
          const matchSlot = allSlots.find(s => s.id === parseInt(slotId));
          setSlot(matchSlot || null);
        }
      } catch { navigate(`/projects/${id}?tab=budget`); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id, budgetId, slotId, navigate]);

  const handleSave = async (payload) => {
    setSaving(true); setAlert(null);
    try {
      const targetSlotId = budgetId ? budget.survey_slot_id : parseInt(slotId);
      const res = await api.post(`/projects/${id}/budgets`, { ...payload, survey_slot_id: targetSlotId });
      setBudget(res.data.data);
      if (isAdmin) {
        setAlert({ type: 'success', text: 'Budget validé' });
        setTimeout(() => navigate(`/projects/${id}?tab=budget`), 1000);
      } else {
        setAlert({ type: 'success', text: 'Budget enregistré' });
        if (isNew && res.data.data?.id) {
          navigate(`/projects/${id}/budgets/${res.data.data.id}/edit`, { replace: true });
        }
      }
    } catch (e) { setAlert({ type: 'error', text: e.response?.data?.message || 'Erreur' }); }
    finally { setSaving(false); }
  };

  const handleSubmit = async (payload) => {
    if (isAdmin) {
      // AG/AGS: save auto-approves, no need for separate submit
      return handleSave(payload);
    }
    setSaving(true); setAlert(null);
    try {
      const targetSlotId = budgetId ? budget.survey_slot_id : parseInt(slotId);
      const res = await api.post(`/projects/${id}/budgets`, { ...payload, survey_slot_id: targetSlotId });
      const bId = res.data.data.id;
      await api.post(`/projects/${id}/budgets/${bId}/submit`);
      setAlert({ type: 'success', text: 'Budget soumis pour approbation' });
      setTimeout(() => navigate(`/projects/${id}?tab=budget`), 1000);
    } catch (e) { setAlert({ type: 'error', text: e.response?.data?.message || 'Erreur' }); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" /></div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          {isNew ? 'Nouveau budget' : 'Modifier le budget'}
        </div>
        <h4 style={{ fontWeight: 700, fontSize: '1.2rem', margin: 0, color: '#111827' }}>
          {slot ? (slot.waveName ? `${slot.waveName} › ${slot.name}` : slot.name) : 'N/A'}
        </h4>
        {project && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{project.name}</span>
            {project.client && (
              <span style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.5 }} />{project.client.name}
              </span>
            )}
          </div>
        )}
      </div>

      {alert && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: '0.82rem',
          background: alert.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${alert.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: alert.type === 'success' ? '#166534' : '#991b1b',
        }}>{alert.text}</div>
      )}

      {/* Editor */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
        <BudgetEditor
          budget={budget}
          slot={slot}
          onSave={handleSave}
          onSubmit={handleSubmit}
          saving={saving}
          isAdmin={isAdmin}
        />
      </div>
    </div>
  );
};

export default BudgetEdit;
